import React, { useCallback, useEffect, useRef, useState } from "react";
import { PillButtonStyles, StyledDropdownMenu } from "./styled";
import LoadingSpinner from "components/PageLoader/Spinner";

const PillButtons = ({
  leftIcon,
  placeholderText,
  mainLabelText,
  rightIcon,
  menuItems = [],
  onSelect,
  onClear,
  size = "M",
  maxWidth,
  statusPlaceholder,
  activeRightIcon,
  customClassName,
  color = "",
  isShowbackground = false,
  search = false,
  statusClassName,
  prefix,
  disabled = false,
  loading = false,
  closeOnLoadComplete = false,
  placeholderClass = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const containerRef = useRef();
  const dropdownRef = useRef();
  const [dropdownAlign, setDropdownAlign] = useState(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);

  const measuredRef = useCallback((el) => {
    containerRef.current = el;
    dropdownRef.current = el;
  }, []);

  // measure  separate effect
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const newAlign = rect.right > window.innerWidth ? "right" : "left";
      setDropdownAlign(newAlign);
    }
    if (!isOpen) {
      setDropdownAlign(null);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => {
      const nextState = !prev;
      if (!nextState) {
        setSearchTerm("");
        setDropdownAlign(null); // reset on close
      }
      return nextState;
    });
  };

  const handleSelect = (item) => {
    onSelect?.(item);

    if (closeOnLoadComplete) {
      setWaitingForResponse(true);
    } else {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    if (closeOnLoadComplete && waitingForResponse && !loading) {
      setIsOpen(false);
      setSearchTerm("");
      setWaitingForResponse(false);
    }
  }, [loading, waitingForResponse, closeOnLoadComplete]);

  // Clear the selection when activeRightIcon is clicked
  const handleClear = (e) => {
    e.stopPropagation(); // Prevent opening dropdown
    if (onClear) {
      onClear();
    } else {
      onSelect?.({ label: "" }); // Send empty value
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const showClearIcon = mainLabelText && activeRightIcon;
  const currentSelectedIcon = showClearIcon ? (
    <span className="right-icon" onClick={handleClear}>
      {activeRightIcon}
    </span>
  ) : (
    rightIcon && <span className="right-icon">{rightIcon}</span>
  );

  const filteredMenuItems = search
    ? menuItems?.filter((item) =>
        item.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : menuItems;

  // If approvedPrefix is true, show "Approved as {label}"
  const displayLabel =
    prefix && mainLabelText ? `${prefix} ${mainLabelText}` : mainLabelText;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Main Pill Button */}
      <PillButtonStyles
        onClick={handleToggle}
        disabled={disabled}
        color={color}
        className={`${isShowbackground ? "show-background" : ""} ${statusClassName || ""} ${disabled ? "is-disabled" : ""}`}
      >
        {leftIcon && <span className="left-icon">{leftIcon}</span>}
        {placeholderText && (
          <span
            className={`placeholder ${!mainLabelText ? statusPlaceholder : ""} ${placeholderClass || ""}`}
          >
            {placeholderText}
            {mainLabelText ? ":" : ""}
          </span>
        )}
        {/* {mainLabelText && <span className="main-label">{mainLabelText}</span>}{" "} */}
        {displayLabel && <span className="main-label">{displayLabel}</span>}
        {currentSelectedIcon}
      </PillButtonStyles>

      {/* Dropdown Menu */}
      {isOpen && menuItems?.length > 0 && (
        <StyledDropdownMenu
          className={`${customClassName || ""} ${dropdownAlign ? `align-${dropdownAlign}` : ""}`}
          size={size}
          style={{ maxWidth: maxWidth }}
          ref={measuredRef}
        >
          {search && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="dropdown-search"
            />
          )}

          {filteredMenuItems?.map((item, index) => {
            const isActive = item.label === mainLabelText; // Check if active
            return (
              <li
                key={index}
                onClick={() => !loading && handleSelect(item)}
                className={isActive ? "selected" : ""}
              >
                <div className="dropdown-content">
                  {item.leftIcon && (
                    <span className="dropdown-left-icon">{item.leftIcon}</span>
                  )}
                  {item.label && (
                    <span className="dropdown-label">{item.label}</span>
                  )}
                </div>

                <div className="dropdown-content">
                  {loading && isActive ? (
                    <LoadingSpinner size={14} color="#A5A5A5" />
                  ) : (
                    <>
                      {item.count !== undefined && (
                        <span className="count">{item.count}</span>
                      )}

                      {item.rightIcon && (
                        <span className="dropdown-right-icon">
                          {item.rightIcon}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </StyledDropdownMenu>
      )}
    </div>
  );
};

export default PillButtons;
